import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, formatTZS } from "@/data/services";
import { Button } from "@/components/ui/button";
import { FinanceTab } from "@/components/admin/FinanceTab";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, ShoppingBag, CreditCard, Repeat, Inbox, LogOut, Loader2, Wallet,
  TrendingUp, Users, Mail, Landmark,
} from "lucide-react";

const COLORS = ["#1273EB", "#F5B301", "#0A2540", "#10B981", "#60A5FA", "#E0A400"];

const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    success: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    new: "bg-brand-blue/15 text-blue-300 border-brand-blue/30",
    failed: "bg-red-500/15 text-red-400 border-red-500/30",
    contacted: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    closed: "bg-gray-500/15 text-gray-300 border-gray-500/30",
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-sm border capitalize ${map[status] || "bg-white/5 text-gray-300 border-white/10"}`}>
      {status}
    </span>
  );
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout } = useAuth();
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setAuthChecked(true);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setAuthChecked(true);
      } catch (e) {
        navigate("/admin");
      }
    })();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, o, sub, c, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/subscriptions"),
        api.get("/admin/contacts"),
        api.get("/admin/payments"),
      ]);
      setStats(s.data); setOrders(o.data); setSubs(sub.data); setContacts(c.data); setPayments(p.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authChecked) loadAll(); }, [authChecked]); // eslint-disable-line

  const updateContact = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    loadAll();
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!authChecked) {
    return <div className="min-h-screen bg-[#06182b] flex items-center justify-center"><Loader2 className="animate-spin text-brand-blue" size={40} /></div>;
  }

  const NAV = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "subscriptions", label: "Subscriptions", icon: Repeat },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "requests", label: "Client Requests", icon: Inbox },
    { id: "finance", label: "Finance", icon: Landmark },
  ];

  const cards = stats ? [
    { label: "Total Revenue", value: formatTZS(stats.total_revenue), icon: Wallet, accent: "text-brand-yellow", bg: "bg-brand-yellow/10" },
    { label: "Paid Orders", value: `${stats.paid_orders}/${stats.total_orders}`, icon: ShoppingBag, accent: "text-brand-blue", bg: "bg-brand-blue/10" },
    { label: "Active Subscriptions", value: stats.active_subscriptions, icon: Repeat, accent: "text-emerald-600", bg: "bg-emerald-500/10" },
    { label: "New Requests", value: stats.new_requests, icon: Inbox, accent: "text-purple-600", bg: "bg-purple-500/10" },
    { label: "Newsletter Subs", value: stats.newsletter_subscribers, icon: Mail, accent: "text-brand-blue", bg: "bg-brand-blue/10" },
    { label: "Total Requests", value: stats.total_requests, icon: Users, accent: "text-white", bg: "bg-gray-500/10" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#06182b] text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-brand-dark fixed inset-y-0 left-0 z-20">
        <div className="p-6 border-b border-white/10 flex justify-center">
          <img src={LOGO_URL} alt="Tanz-Bridge" className="h-11 object-contain rounded-md bg-white p-1" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.id} data-testid={`admin-tab-${n.id}`} onClick={() => setTab(n.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${
                  tab === n.id ? "bg-brand-blue text-white" : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}>
                <Icon size={18} /> {n.label}
                {n.id === "requests" && stats?.new_requests > 0 && (
                  <span data-testid="requests-badge"
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                    {stats.new_requests}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3 px-2">
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
            <div className="min-w-0">
              <p className="text-sm truncate text-white">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button data-testid="admin-logout" onClick={doLogout} variant="outline"
            className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm text-sm">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 md:ml-64">
        {/* Mobile tab bar */}
        <div className="md:hidden flex overflow-x-auto gap-2 p-3 border-b border-white/10 bg-brand-dark sticky top-0 z-20">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`admin-mobile-tab-${n.id}`} onClick={() => setTab(n.id)}
              className={`relative px-3 py-2 rounded-sm text-xs whitespace-nowrap ${tab === n.id ? "bg-brand-blue text-white" : "bg-white/10 text-gray-300"}`}>
              {n.label}
              {n.id === "requests" && stats?.new_requests > 0 && (
                <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold items-center justify-center align-middle">
                  {stats.new_requests}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6 lg:p-10 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-extrabold capitalize text-white">{tab === "requests" ? "Client Requests" : tab}</h1>
              <p className="text-gray-500 text-sm mt-1">Welcome back, {user?.name?.split(" ")[0]}.</p>
            </div>
            <Button onClick={loadAll} variant="outline" className="border-white/20 bg-transparent text-white rounded-sm hidden sm:flex hover:bg-white/10" data-testid="admin-refresh">
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-brand-blue" size={36} /></div>
          ) : (
            <>
              {tab === "overview" && stats && (
                <div className="space-y-8" data-testid="admin-overview">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {cards.map((c) => {
                      const Icon = c.icon;
                      return (
                        <div key={c.label} className="rounded-lg bg-white/5 border border-white/10 p-6">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs tracking-wide uppercase text-gray-400">{c.label}</span>
                            <span className={`h-9 w-9 rounded-sm flex items-center justify-center ${c.bg}`}><Icon size={18} className={c.accent} /></span>
                          </div>
                          <p className="text-2xl font-heading text-white">{c.value}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-lg bg-white/5 border border-white/10 p-6">
                      <h3 className="font-heading mb-6 flex items-center gap-2 text-white"><TrendingUp size={18} className="text-brand-blue" /> Revenue by Source</h3>
                      {stats.revenue_by_service.length === 0 ? (
                        <p className="text-gray-400 text-sm py-16 text-center">No revenue yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={stats.revenue_by_service}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                            <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
                            <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `${v / 1000}k`} />
                            <Tooltip cursor={false} contentStyle={{ background: "#0A2540", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "#fff" }}
                              itemStyle={{ color: "#fff" }} labelStyle={{ color: "#fff" }}
                              formatter={(v) => formatTZS(v)} />
                            <Bar dataKey="value" fill="#1273EB" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <div className="rounded-lg bg-white/5 border border-white/10 p-6">
                      <h3 className="font-heading mb-6 flex items-center gap-2 text-white"><CreditCard size={18} className="text-brand-yellow" /> Revenue Split</h3>
                      {stats.revenue_by_service.length === 0 ? (
                        <p className="text-gray-400 text-sm py-16 text-center">No revenue yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={stats.revenue_by_service} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                              {stats.revenue_by_service.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ background: "#0A2540", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "#fff" }}
                              itemStyle={{ color: "#fff" }} labelStyle={{ color: "#fff" }}
                              formatter={(v) => formatTZS(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {tab === "orders" && (
                <Table testid="admin-orders-table" empty="No orders yet."
                  head={["Order", "Service", "Customer", "Phone", "Amount", "Status", "Date"]}
                  rowKeys={orders.map((o) => o.id)}
                  rows={orders.map((o) => [
                    <span className="font-mono text-xs text-gray-500">{o.id}</span>, o.service_name, o.customer_name, o.phone,
                    formatTZS(o.amount), <StatusBadge status={o.status} />, fmtDate(o.created_at),
                  ])} />
              )}

              {tab === "subscriptions" && (
                <Table testid="admin-subs-table" empty="No subscriptions yet."
                  head={["Plan", "Customer", "Email", "Phone", "Amount/mo", "Status", "Date"]}
                  rowKeys={subs.map((s) => s.id)}
                  rows={subs.map((s) => [
                    <span className="font-medium text-white">{s.plan}</span>, s.customer_name, s.email, s.phone,
                    formatTZS(s.amount), <StatusBadge status={s.status} />, fmtDate(s.created_at),
                  ])} />
              )}

              {tab === "payments" && (
                <Table testid="admin-payments-table" empty="No payments yet."
                  head={["Receipt", "Method", "Reference", "Phone", "Amount", "Status", "Date"]}
                  rowKeys={payments.map((p) => p.id)}
                  rows={payments.map((p) => [
                    <span className="font-mono text-xs text-brand-blue">{p.mpesa_receipt || "—"}</span>,
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#E60000]" /> {p.method}</span>,
                    <span className="capitalize text-xs">{p.reference_type}</span>, p.phone,
                    formatTZS(p.amount), <StatusBadge status={p.status} />, fmtDate(p.created_at),
                  ])} />
              )}

              {tab === "finance" && <FinanceTab />}

              {tab === "requests" && (
                <Table testid="admin-requests-table" empty="No client requests yet."
                  head={["Name", "Email", "Phone", "Interest", "Message", "Status", "Action"]}
                  rowKeys={contacts.map((c) => c.id)}
                  rows={contacts.map((c) => [
                    c.name, c.email, c.phone, c.service_interest || "—",
                    <span className="block max-w-[220px] truncate text-gray-500">{c.message}</span>,
                    <StatusBadge status={c.status} />,
                    <select data-testid={`request-status-${c.id}`} value={c.status} onChange={(e) => updateContact(c.id, e.target.value)}
                      className="bg-[#0A2540] border border-white/20 text-white rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-brand-blue">
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>,
                  ])} />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const Table = ({ head, rows, empty, testid, rowKeys }) => (
  <div className="rounded-lg bg-white/5 border border-white/10 overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/5">
            {head.map((h) => <th key={h} className="text-left px-4 py-4 text-xs uppercase tracking-wide text-gray-400 font-medium whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="text-center py-16 text-gray-400">{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={rowKeys?.[i] ?? i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              {r.map((cell, j) => <td key={`${rowKeys?.[i] ?? i}-${head[j]}`} className="px-4 py-4 text-gray-300 whitespace-nowrap">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
